import React, { useState, useEffect } from 'react'
import { Calendar, Clock, X, Check } from 'lucide-react'
import { motion } from 'framer-motion'
import { format, parseISO, formatISO, isValid } from 'date-fns'
import { cn } from '../../utils/cn'
import ModernCalendar from './ModernCalendar'

const ModernDateTimeInput = ({
  value,
  onChange,
  placeholder = "Select date and time",
  className = "",
  disabled = false,
  includeTime = true,
  showIcon = true,
  dateFormat = "MMM d, yyyy",
  timeFormat = "h:mm a",
  error = false,
  useAbsolutePositioning = false,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('')

  // Update display value when value changes
  useEffect(() => {
    if (value) {
      try {
        const date = typeof value === 'string' ? parseISO(value) : value
        if (isValid(date)) {
          if (includeTime) {
            setDisplayValue(`${format(date, dateFormat)} at ${format(date, timeFormat)}`)
          } else {
            setDisplayValue(format(date, dateFormat))
          }
        } else {
          setDisplayValue('')
        }
      } catch (error) {
        setDisplayValue('')
      }
    } else {
      setDisplayValue('')
    }
  }, [value, dateFormat, timeFormat, includeTime])

  const handleChange = (newValue) => {
    onChange(newValue)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange('')
  }

  return (
    <div className={cn("relative", className)}>
      <ModernCalendar
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        includeTime={includeTime}
        useAbsolutePositioning={useAbsolutePositioning}
        className="w-full"
        {...props}
      />
      
      {/* Clear button */}
      {value && !disabled && (
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleClear}
          className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
        >
          <X className="h-3 w-3 text-neutral-400 dark:text-neutral-500" />
        </motion.button>
      )}
    </div>
  )
}

// Individual Date Input (no time)
export const ModernDateInput = (props) => {
  return (
    <ModernDateTimeInput
      {...props}
      includeTime={false}
      placeholder="Select date"
    />
  )
}

// Individual Time Input (no date)
export const ModernTimeInput = ({
  value,
  onChange,
  placeholder = "Select time",
  className = "",
  disabled = false,
  format24Hour = false,
  ...props
}) => {
  const [timeValue, setTimeValue] = useState('')

  useEffect(() => {
    if (value) {
      try {
        const date = typeof value === 'string' ? parseISO(value) : value
        if (isValid(date)) {
          setTimeValue(format(date, format24Hour ? 'HH:mm' : 'h:mm a'))
        } else {
          setTimeValue('')
        }
      } catch (error) {
        setTimeValue('')
      }
    } else {
      setTimeValue('')
    }
  }, [value, format24Hour])

  const handleTimeChange = (e) => {
    const timeStr = e.target.value
    if (timeStr) {
      // Create a date object with today's date and the selected time
      const [hours, minutes] = timeStr.split(':').map(Number)
      const now = new Date()
      const timeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes)
      onChange(formatISO(timeDate))
    } else {
      onChange('')
    }
  }

  const handleClear = () => {
    onChange('')
  }

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <input
          type="time"
          value={value ? format(typeof value === 'string' ? parseISO(value) : value, 'HH:mm') : ''}
          onChange={handleTimeChange}
          disabled={disabled}
          className={cn(
            'w-full px-3 py-2 pl-10 border rounded-lg transition-all duration-200',
            'bg-white dark:bg-[#221f2f] border-neutral-300 dark:border-[#2f2b41]',
            'hover:border-neutral-400 dark:hover:border-neutral-500',
            'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent',
            'text-neutral-900 dark:text-neutral-100',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          placeholder={placeholder}
          {...props}
        />
        
        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
        
        {value && !disabled && (
          <motion.button
            type="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
          >
            <X className="h-3 w-3 text-neutral-400 dark:text-neutral-500" />
          </motion.button>
        )}
      </div>
    </div>
  )
}

export default ModernDateTimeInput